/** @format */

import React, {useCallback, useContext, useEffect} from 'react';

import {PreviouslyUsedShapeSelect} from './previously_used_shape_select';
import {
    QUOTER_SHAPE_PREVIOUSLY_USED,
    SHAPE_CARTON_AUTOLOCK,
    SHAPE_CARTON_CUSTOM,
    SHAPE_CARTON_STRAIGHT_TUCK,
    SUBSTRATE_BACKING_PAPER,
    SUBSTRATE_BACKING_POLY
} from '../../utilities/constants';
import SVGDownload from 'frontier/ds/quoter/svg_download';
import useDieSelection from './hooks/use_die_selection';
import CustomShapeSelect from './custom_shape_select';
import AdditionalOptions from './additional_options';
import ApplicationSelect from './application_select';
import ArtworkUploader from './artwork_uploader';
import MaterialSelect from './material_select';
import LaminateSelect from './laminate_select';
import {QuoteContext} from 'frontier-quoter';
import {Input} from '@sttark/design-system';
import OrderSummary from './order_summary';
import SelectedDie from './selected_die';
import ShapeSelect from './shape_select';
import SizeSelect from './size_select';
import Section from './section';
import {
    FORM_FACTOR_HAND_APPLIED,
    NO_LAMINATE_SUBSTRATES,
    SIZE_TYPE_STANDARD
} from 'frontier-constants';

import backingPaper from './assets/backing-paper.svg';
import backingPoly from './assets/backing-poly.svg';
import shapeAutoLock from 'frontier/ds/quoter/assets/shape-autolock.svg';
import shapeStraightTuck from 'frontier/ds/quoter/assets/shape-straigttuck.svg';
import shapeCustomCarton from 'frontier/ds/quoter/assets/shape-carton-custom.svg';
import {CardSelect} from '@sttark/design-system';

function LabelQuoter() {
    const ctx = useContext(QuoteContext);
    const order = ctx.order;
    const {errors} = ctx.handleIsComplete(order);

    const {
        diesMatchingShapeOppositeCorners,
        setCustomShapeSubCategory,
        customShapeSubCategory,
        diesMatchingShape,
        setShapeCategory,
        setWidthLength,
        shapeCategory,
        isCustomShape,
        selectedDie,
        setCorners,
        setLength,
        setWidth,
        isNewDie,
        allDies,
        corners,
        length,
        width
    } = useDieSelection({
        order: ctx.order,
        updateOrder: patch => ctx.handleChange({order: {$merge: patch}})
    });

    const isLaminateRequired = NO_LAMINATE_SUBSTRATES.indexOf(ctx.order.substrate_id) === -1;

    const enabledSections = {};
    enabledSections.laminate = !!order.substrate_id && isLaminateRequired;
    enabledSections.shape = isLaminateRequired ? !!order.laminate_id : !!order.substrate_id;
    enabledSections.size = enabledSections.shape && !!shapeCategory && allDies.length > 0;
    enabledSections.application = enabledSections.size && !!order.width && !!order.length;
    // We MUST have an order id before we can upload artwork
    enabledSections.artwork =
        enabledSections.application && order.form_factor_id != null && order.id > 0;

    const handleMaterialChange = useCallback(
        v => ctx.handleChangeMaterial(v),
        [ctx.handleChangeMaterial]
    );

    const handleLaminateChange = useCallback(
        v => ctx.handleChange({order: {laminate_id: {$set: v}}}),
        [ctx.handleChange, ctx.handleChangeMaterial]
    );

    useEffect(() => {
        checkDies();
    }, [corners]);

    // we prefer to use taller dies over wider dies, so if the user has selected 'hand applied' rolls, we will default to taller dies
    const checkDies = useCallback(() => {
        //check for existing reciprocal of currently selected die
        const diesMatchingReciprocal = diesMatchingShape.filter(
            ({width: listWidth, length: listLength}) => width == listLength && length == listWidth
        );

        // if there is no existing die for current measurements, but there is a reciprocal die, choose the reciprocal
        if (diesMatchingReciprocal.length > 0 && !order?.size_id > 0) {
            setWidthLength(diesMatchingReciprocal[0].width, diesMatchingReciprocal[0].length);
            // if there is an existing die for current measurements, and a reciprocal, use the taller of the two
        } else if (diesMatchingReciprocal.length > 0 && order?.size_id > 0) {
            if (diesMatchingReciprocal[0].length > length) {
                setWidthLength(diesMatchingReciprocal[0].width, diesMatchingReciprocal[0].length);
            }
            // if neither die exists, make the length the larger value, as it is better for printing
        } else if (!order?.size_id > 0) {
            if (width > length) setWidthLength(length, width);
        }
    }, [width, length, diesMatchingShape]);

    const optimizeDieSize = useCallback(() => {
        if (order.form_factor_id === FORM_FACTOR_HAND_APPLIED && length && width) {
            checkDies();
        }
    }, [order.form_factor_id, length, width, diesMatchingShape, checkDies]);

    const handleApplicationChange = useCallback(
        v => {
            ctx.handleChange({order: {form_factor_id: {$set: v}}});
            if (v === FORM_FACTOR_HAND_APPLIED) {
                checkDies();
            }
        },
        [ctx.handleChange, diesMatchingShape, width, length]
    );

    const handleRollDirectionChange = useCallback(
        v => ctx.handleChange({order: {roll_direction_id: {$set: v}}}),
        [ctx.handleChange]
    );

    const handleDieSelected = useCallback(
        v =>
            ctx.handleChange({
                order: {size_id: {$set: v}, size_type_id: {$set: SIZE_TYPE_STANDARD}}
            }),
        [ctx.handleChange]
    );

    const handleOrderNotes = v => {
        ctx.handleChange({order: {comments_customer: {$set: v}}});
    };

    // A "custom shape" is a shape that isn't rectangular or circular, where we display a
    // specific list of dies from our inventory.
    const isSelectingExistingCustomShape = isCustomShape && !isNewDie;
    const isSelectingPreviouslyUsed = shapeCategory === QUOTER_SHAPE_PREVIOUSLY_USED;
    const shouldShowSizeInputs = !isSelectingExistingCustomShape && !isSelectingPreviouslyUsed;

    return (
        <div className="row g-lg-7">
            <div className="col-lg-8">
                <Section>
                    <h3 className="mt-3">Select Your Material</h3>
                    <MaterialSelect value={order.substrate_id} onChange={handleMaterialChange} />
                </Section>

                <Section visible={enabledSections.laminate}>
                    <h5>Laminate</h5>
                    <p>Select a laminate type</p>
                    <LaminateSelect
                        onChange={handleLaminateChange}
                        substrate={order.substrate_id}
                        value={order.laminate_id}
                    />
                </Section>

                <Section enabled={enabledSections.shape}>
                    <ShapeSelect
                        onCustomShapeSubCategoryChange={setCustomShapeSubCategory}
                        customShapeSubCategory={customShapeSubCategory}
                        onShapeCategoryChange={setShapeCategory}
                        shapeCategory={shapeCategory}
                        onCornersChange={setCorners}
                        corners={corners}
                    />
                </Section>

                <Section enabled={enabledSections.size}>
                    {isSelectingExistingCustomShape && diesMatchingShape.length > 0 && (
                        <>
                            <h4>Choose a Shape</h4>
                            <CustomShapeSelect
                                onChange={handleDieSelected}
                                selectedDie={selectedDie}
                                dies={diesMatchingShape}
                            />
                        </>
                    )}
                    {isSelectingPreviouslyUsed && (
                        <>
                            <h4>Previously Used Custom Shapes</h4>
                            <PreviouslyUsedShapeSelect
                                onChange={handleDieSelected}
                                selectedDie={selectedDie}
                            />
                        </>
                    )}
                    {shouldShowSizeInputs && (
                        <>
                            <h3>Select Your Size</h3>
                            <SizeSelect
                                onBlurFunc={optimizeDieSize}
                                onLengthChange={setLength}
                                dies={diesMatchingShape}
                                onWidthChange={setWidth}
                                errors={errors}
                                length={length}
                                width={width}
                            />
                        </>
                    )}
                    <SelectedDie
                        errors={errors}
                        diesMatchingShapeOppositeCorners={diesMatchingShapeOppositeCorners}
                        quotedDieCharge={ctx.order.quoted_die_charge}
                        productId={order.product_type_id}
                        onSetCorners={setCorners}
                        isNewDie={isNewDie}
                        die={selectedDie}
                        corners={corners}
                    />
                    <SVGDownload
                        errors={errors}
                        isSquareCorners={order.is_square_corners ? 1 : 0}
                        shapeId={order.shape_id}
                        length={order.length}
                        width={order.width}
                        depth={order.depth}
                    />
                </Section>

                <Section enabled={enabledSections.application}>
                    <h3>Select Application</h3>
                    <ApplicationSelect
                        onRollDirectionChange={handleRollDirectionChange}
                        rollDirection={order.roll_direction_id}
                        onChange={handleApplicationChange}
                        value={order.form_factor_id}
                    />
                </Section>

                <Section enabled={enabledSections.artwork}>
                    <h3>Upload Artwork</h3>
                    <ArtworkUploader
                        orderId={order.id}
                        isDuplex={order.is_duplex}
                        isFrontBack={order.is_front_back}
                        lastUpdate={ctx.lastUpdate}
                    />

                    <h6 className="mt-6">Additional Options</h6>
                    <AdditionalOptions errors={errors} />
                    {order.is_customizable_backing && (
                        <>
                            <h6>Backing Choice</h6>
                            <CardSelect
                                options={[
                                    {
                                        label: 'Paper Backing',
                                        value: SUBSTRATE_BACKING_PAPER,
                                        image: backingPaper,
                                        description:
                                            'Paper backing is biodegradable with a naturally fibrous texture, suitable for various label applications where environmental impact is a consideration.'
                                    },
                                    {
                                        label: 'Poly (PET) Backing',
                                        value: SUBSTRATE_BACKING_POLY,
                                        image: backingPoly,
                                        description:
                                            'Poly (PET) backing is durable, resistant to water and chemicals, providing a stronger support for labels used in harsh environments and is fully recyclable.'
                                    }
                                ]}
                                value={parseInt(order.substrate_backing_id)}
                                onChange={v => {
                                    ctx.handleChange({
                                        order: {substrate_backing_id: {$set: v}}
                                    });
                                }}
                            />
                        </>
                    )}
                    <h6 className="mt-6">Additional Order Notes</h6>
                    <Input
                        value={order.comments_customer || ''}
                        onChange={handleOrderNotes}
                        label="Order Notes"
                        rows={4}
                    />
                </Section>
            </div>

            <div className="col-lg-4">
                <div className=" pt-5 sticky-top">
                    <h4 className="mt-0">Order Summary</h4>
                    <OrderSummary isCustomFee={order.size_type_id !== SIZE_TYPE_STANDARD} />
                </div>
            </div>
        </div>
    );
}

export default LabelQuoter;
